/* eslint react/prop-types: 0 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import ApiService from '../../common/ApiCalls';
import DataView from '../../v2/components/DataView';
import {
  correctionError,
  editorValue,
  historyPermissions
} from './historyCorrections';

const statusLabels = {
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected'
};

function ReviewValue({ field, value, state, label }) {
  if (state !== 'available')
    return (
      <Typography>
        {label}: {state === 'cleared' ? 'Cleared' : 'No recorded value'}
      </Typography>
    );
  if (!field)
    return (
      <Typography>
        {label}: {JSON.stringify(value)}
      </Typography>
    );
  return (
    <DataView
      card={{
        items: [
          {
            item_type: 'field',
            field_id: 'preview',
            field: { ...field, label }
          }
        ]
      }}
      values={{ preview: editorValue(field, value) }}
      historyStates={{ preview: 'available' }}
      orientation="horizontal"
      isEdit={false}
    />
  );
}

function CorrectionRow({ record, onReviewed, onBusy }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(null);
  const inFlight = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const review = async (decision) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    onBusy(true);
    setError(null);
    const payload = retry || {
      decision,
      expected_version: record.version,
      ...(reason.trim() ? { reason: reason.trim() } : {})
    };
    try {
      await ApiService().reviewHistoryCorrection(record.id, payload);
      if (active.current)
        onReviewed(
          payload.decision === 'approve'
            ? 'Correction approved and applied.'
            : 'Correction rejected. Values are unchanged.'
        );
    } catch (e) {
      if (active.current) {
        setError(correctionError(e));
        setRetry(
          !e?.response?.status || e.response.status >= 500 ? payload : null
        );
      }
    } finally {
      inFlight.current = false;
      if (active.current) {
        setBusy(false);
        onBusy(false);
      }
    }
  };
  const p = record.proposal;
  return (
    <Box
      component="article"
      sx={{ border: '1px solid', borderColor: 'divider', p: 2, mb: 2 }}
    >
      <Typography variant="body2">{record.subject_name || record.proposal.subject_id}</Typography>
      <Typography variant="h6">{record.field?.label || p.field_id}</Typography>
      <Typography>
        {statusLabels[record.status]} · Effective {p.effective_day} (
        {p.history_timezone})
      </Typography>
      <Typography variant="body2">
        {p.object_type === 'asset'
          ? 'Location'
          : p.object_type === 'entity'
            ? 'Entity'
            : `${p.object_type}: ${record.object_name || p.object_id}`}
      </Typography>
      <Typography variant="body2">
        Submitted {new Date(record.created_at).toLocaleString()} · By{' '}
        {record.actor_name || record.actor_id}
      </Typography>
      <ReviewValue
        field={record.field}
        value={record.before.value}
        state={record.before.data_state}
        label="Before submission"
      />
      <ReviewValue
        field={record.field}
        value={p.value}
        state={p.is_clear ? 'cleared' : 'available'}
        label="Proposed value"
      />
      {record.review_reason && (
        <Typography>Review reason: {record.review_reason}</Typography>
      )}
      {record.reviewer_id && (
        <Typography variant="body2">
          Reviewed by {record.reviewer_name || record.reviewer_id}
        </Typography>
      )}
      {record.approval_method === 'bypass' && (
        <Typography variant="body2">
          Applied using Historical Data Admin permission.
        </Typography>
      )}
      {record.approval_method === 'current_day' && (
        <Typography variant="body2">
          Applied on the location’s current day.
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ my: 1 }}>
          {error}
        </Alert>
      )}
      {record.can_review && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Review reason (optional)"
            multiline
            value={reason}
            disabled={busy || !!retry}
            inputProps={{ maxLength: 2000 }}
            onChange={(e) => setReason(e.target.value)}
          />
          {retry ? (
            <Button disabled={busy} onClick={() => review(retry.decision)}>
              Retry {retry.decision}
            </Button>
          ) : (
            <>
              <Button disabled={busy} onClick={() => review('approve')}>
                Approve correction
              </Button>
              <Button
                disabled={busy}
                color="error"
                onClick={() => review('reject')}
              >
                Reject correction
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export function HistoryCorrectionsPanel({ subjectId, subjectType, onClose, onChanged, embedded = true }) {
  const canReview = historyPermissions(subjectId).review;
  const [mine, setMine] = useState(!canReview);
  const [status, setStatus] = useState('pending_approval');
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const changed = useRef(false);
  const key = `${subjectType}:${subjectId}:${mine}:${status}:${page}:${attempt}`;
  useEffect(() => {
    let active = true;
    ApiService()
      .listHistoryCorrections({ subjectId, subjectType, mine, status, page })
      .then(
        (data) => {
          if (active) setResult({ key, ...data });
        },
        (error) => {
          if (active)
            setResult({
              key,
              error:
                error?.response?.status === 403
                  ? correctionError(error)
                  : 'Unable to load corrections. Please retry.'
            });
        }
      );
    return () => {
      active = false;
    };
  }, [subjectId, subjectType, mine, status, page, key]);
  const close = () => {
    if (changed.current) onChanged();
    onClose();
  };
  const loaded = result?.key === key;
  const Container = embedded ? Box : Dialog;
  return (
    <Container {...(embedded ? { sx: { p: 2, overflow: 'auto', height: '100%' } } : { open: true, onClose: busy ? undefined : close, fullWidth: true, maxWidth: 'md' })} aria-labelledby="history-queue-title">
      <DialogTitle id="history-queue-title">
        Historical corrections{subjectId ? ` for this ${subjectType === 'entity' ? 'entity' : 'location'}` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, py: 1 }}>
          {canReview && (
            <TextField
              select
              label="Show"
              value={String(mine)}
              disabled={busy}
              onChange={(e) => {
                setMine(e.target.value === 'true');
                setPage(1);
              }}
            >
              <MenuItem value="false">All permitted submissions</MenuItem>
              <MenuItem value="true">My submissions</MenuItem>
            </TextField>
          )}
          <TextField
            select
            label="Status"
            value={status}
            disabled={busy}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            {Object.entries(statusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
            <MenuItem value="">All statuses</MenuItem>
          </TextField>
          <Button disabled={busy} onClick={() => setAttempt((n) => n + 1)}>
            Refresh
          </Button>
        </Box>
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {!loaded ? (
          <Typography role="status">Loading corrections…</Typography>
        ) : result.error ? (
          <Alert severity="error">{result.error}</Alert>
        ) : (
          <>
            {!result.data.length && (
              <Typography>No corrections found.</Typography>
            )}
            <fieldset
              disabled={busy}
              style={{ border: 0, padding: 0, margin: 0 }}
            >
              {result.data.map((record) => (
                <CorrectionRow
                  key={`${key}:${record.id}`}
                  record={record}
                  onBusy={setBusy}
                  onReviewed={(text) => {
                    changed.current = true;
                    setBusy(false);
                    setMessage(text);
                    setAttempt((n) => n + 1);
                  }}
                />
              ))}
            </fieldset>
            <Button
              disabled={busy || page <= 1}
              onClick={() => setPage((n) => n - 1)}
            >
              Previous page
            </Button>
            <Typography component="span">
              Page {page} · {result.pagination.total} corrections
            </Typography>
            <Button
              disabled={busy || page * 10 >= result.pagination.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Next page
            </Button>
          </>
        )}
      </DialogContent>
      {!embedded && <DialogActions>
        <Button disabled={busy} onClick={close}>
          Close
        </Button>
      </DialogActions>}
    </Container>
  );
}

export default function HistoryCorrectionsQueue({
  subjectId,
  subjectType = 'asset',
  onChanged
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Historical corrections</Button>
      {open && (
        <HistoryCorrectionsPanel
          embedded={false}
          key={`${subjectType}:${subjectId}`}
          subjectId={subjectId}
          subjectType={subjectType}
          onChanged={onChanged}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
